import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

/**
 * Returns today's date value.
 *
 * @returns {dayjs.Dayjs} A "dayjs" instance representing the today's date.
 */
const TODAY = () => dayjs().toDate();

/**
 * Returns a day after tomorrow date value based on the input parameter `fromDate`.
 *
 * @param {string|number|Date|dayjs.Dayjs} [fromDate=TODAY()] - The date value to be used as a base for calculation.
 * If not provided then {@link TODAY} is used as a base starting point.
 *
 * @returns {dayjs.Dayjs} A "dayjs" instance representing the parsed date.
 * Returns an invalid "dayjs" object if parsing fails (check with `.isValid()`).
 */
const TOMORROW = (fromDate = TODAY()) => dayjs(fromDate).add(1, 'day').toDate();

/**
 * Returns a two days later date value based on the input parameter `fromDate`.
 *
 * @param {string|number|Date|dayjs.Dayjs} [fromDate=TODAY()] - The date value to be used as a base for calculation.
 * If not provided then {@link TODAY} is used as a base starting point.
 *
 * @returns {dayjs.Dayjs} A "dayjs" instance representing the parsed date.
 * Returns an invalid "dayjs" object if parsing fails (check with `.isValid()`).
 */
const TWO_DAYS_LATER = (fromDate = TODAY()) => dayjs(fromDate).add(2, 'day').toDate();

/**
 * Returns a 21 days later date value based on the input parameter `fromDate`.
 *
 * @param {string|number|Date|dayjs.Dayjs} [fromDate=TODAY()] - The date value to be used as a base for calculation.
 * If not provided then {@link TODAY} is used as a base starting point.
 *
 * @returns {dayjs.Dayjs} A "dayjs" instance representing the parsed date.
 * Returns an invalid "dayjs" object if parsing fails (check with `.isValid()`).
 */
const TWENTY_ONE_DAYS_LATER = (fromDate = TODAY()) => dayjs(fromDate).add(21, 'day').toDate();

/**
 * Date formats used for `Date` formatting into a `String` value for various purposes.
 * `STORAGE` - for Application service and DB saving; also for external services APIs, other cases.
 * `DISPLAY` - typically for UI components.
 * `ISO` - various cases involving a mix of the above.
 */
const DATE_FORMATS = Object.freeze({
  STORAGE: 'YYYY-MM-DD',
  DISPLAY: 'DD.MM.YYYY',
  ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
});

/**
 * Converts a date value to a formatted date string using "dayjs".
 *
 * @param {string|number|Date|dayjs.Dayjs} date - The input date to format.
 * Can be a JavaScript Date object, ISO date string, timestamp, or a "dayjs" instance.
 * @param {string} [format=DATE_FORMATS.STORAGE] - The output format string.
 * Prefer using one of the predefined formats from {@link DATE_FORMATS}
 * (e.g., {@link DATE_FORMATS.DISPLAY}, {@link DATE_FORMATS.ISO}).
 * Custom "dayjs" format tokens may be provided when needed.
 * Defaults to {@link DATE_FORMATS.STORAGE} ('YYYY-MM-DD').
 *
 * @returns {string} The formatted date string.
 *
 * @example
 * // Using the default storage format
 * dateToString('2025-11-07T16:16:00.000Z');
 * // → '2025-11-07'
 *
 * @example
 * // UI display format
 * dateToString(new Date(), DATE_FORMATS.DISPLAY);
 * // → '29.12.2025' (example)
 *
 * @example
 * // Full ISO format
 * dateToString(dayjs(), DATE_FORMATS.ISO);
 * // → '2025-12-29T14:30:45.123Z' (example)
 *
 * @see DATE_FORMATS for available predefined format strings.
 */
const dateToString = (date, format = DATE_FORMATS.STORAGE) => {
  return dayjs(date).format(format);
};

/**
 * Converts a date string (or compatible value) to a native JavaScript Date object.
 *
 * Parses the input using "dayjs" with the specified format and returns a valid Date object
 * only if parsing succeeds. Returns null on invalid or unparseable input.
 * Useful for safely converting formatted date strings (e.g., from storage or user input)
 * into native Date instances.
 *
 * @param {string|number|Date|dayjs.Dayjs} value - The date value to convert.
 * Can be a date string, timestamp, existing Date object, or dayjs instance.
 * @param {string} [format=DATE_FORMATS.STORAGE] - The expected input format.
 * Prefer using one of the predefined formats from {@link DATE_FORMATS}
 * (e.g., {@link DATE_FORMATS.STORAGE}, {@link DATE_FORMATS.DISPLAY}).
 * Custom format tokens can be used when necessary.
 * Defaults to {@link DATE_FORMATS.STORAGE} ('YYYY-MM-DD').
 *
 * @returns {Date|null} A native JavaScript Date object if the input is valid according to the format;
 * otherwise null.
 *
 * @example
 * // Valid storage format string
 * convertToDateFormat('2025-12-29');
 * // → Date object for 2025-12-29T00:00:00 (local or system timezone)
 *
 * @example
 * // Using display format from UI input
 * convertToDateFormat('29.12.2025', DATE_FORMATS.DISPLAY);
 * // → Date object for 2025-12-29T00:00:00
 *
 * @example
 * // Invalid input returns null
 * convertToDateFormat('invalid-date', DATE_FORMATS.STORAGE);
 * // → null
 *
 * @example
 * // Passing an existing Date object (pass-through)
 * convertToDateFormat(new Date());
 * // → the same (or equivalent) Date object
 *
 * @see DATE_FORMATS for available predefined format strings.
 */
const convertToDateFormat = (value, format = DATE_FORMATS.STORAGE) => {
  const dayjsObject = dayjs(value, format);

  return dayjsObject.isValid()
    ? dayjsObject.toDate()
    : null;
};

/**
 * Parses a date string or any other date value through "dayjs" and returns a "dayjs" object.
 *
 * Interprets the input according to the provided format (if specified). This is useful
 * for consistent parsing when loading dates from storage, APIs, or user input.
 *
 * @param {string|number|Date|dayjs.Dayjs} date - The date value to parse.
 * Can be an ISO string, timestamp, native Date object, or existing "dayjs" instance.
 * @param {string} [format] - Optional format string describing the input pattern.
 * Prefer using one of the predefined formats from {@link DATE_FORMATS}
 * (e.g., {@link DATE_FORMATS.DISPLAY}, {@link DATE_FORMATS.STORAGE}) when the input
 * is expected to match a known structure. Custom "dayjs" format tokens may be used
 * if needed.
 *
 * @returns {dayjs.Dayjs} A "dayjs" instance representing the parsed date.
 * Returns an invalid "dayjs" object if parsing fails (check with `.isValid()`).
 *
 * @example
 * // Parse an ISO 8601 string (default behavior works well)
 * parseDate('2025-11-27T16:16:00.000Z');
 * // → valid "dayjs" instance for 2025-11-27 16:16:00
 *
 * @example
 * // Parse a display-formatted string from UI input
 * parseDate('29.12.2025', DATE_FORMATS.DISPLAY);
 * // → "dayjs" instance for 2025-12-29T00:00:00
 *
 * @example
 * // Parse a storage-formatted date string
 * parseDate('2025-11-27', DATE_FORMATS.STORAGE);
 * // → "dayjs" instance for 2025-11-27T00:00:00
 *
 * @example
 * // Pass through an existing Date or "dayjs" object
 * parseDate(new Date());
 * // → equivalent "dayjs" instance
 *
 * @see DATE_FORMATS for available predefined format strings.
 */
const parseDate = (date, format = DATE_FORMATS.STORAGE) => dayjs(date, format);

export {
  convertToDateFormat,
  DATE_FORMATS,
  dateToString,
  parseDate,
  TODAY,
  TOMORROW,
  TWENTY_ONE_DAYS_LATER,
  TWO_DAYS_LATER,
};
